import { h } from 'preact';
import PropTypes from 'prop-types';

import { getClassNames } from '../../../../utils/classnames-utils';
import { getUrlWithParam } from '../../../../utils/build-urls';
import { getLabelText, truncateText } from '../../../../utils/string-utils';
import { IconGallery, IconPlay } from '../../1-foundation/icons/icons';
import Wrapper from '../../1-foundation/wrapper/wrapper';
import Label from '../../2-atoms/label/label';

import { modifierList } from './props-list';

const config = require('config');

const lazyLoadingClass = config.lazyLoadingContents.lazyClass;

const DEFAULT_NEWS_DATA = {};
const DEFAULT_CARD_MODIFIERS = [];
const DEFAULT_SHOW_CHANNEL = true;
const DEFAULT_TRUNCATE_TITLE = true;
const DEFAULT_TARGET = '_blank';
export const ASPECT_RATIO_16_9 = '16-9';
export const ASPECT_RATIO_16_10 = '16-10';

const renderImage = ({ urlImage, hasVideo, hasGallery, imageAspectRatio, videoDuration, isParsely }) => {
  if (!urlImage) {
    return null;
  }

  const imgProps = {
    crossOrigin: 'anonymous',
    className: `c-card__img ${isParsely ? 'parselyImg' : ''} ${lazyLoadingClass}`,
    src: urlImage,
    loading: 'lazy',
    sizes: '(max-width: 1920px) 100vw',
    srcSet: `${urlImage} 1x, ${urlImage} 2x`,
    'data-srcset': undefined,
    'data-src': undefined,
  };

  return (
    <div className="c-card__img-wrapper">
      <figure className={`o-aspect-ratio o-aspect-ratio--${imageAspectRatio}`}>
        <img {...imgProps} alt="" />
      </figure>
      {videoDuration && <div className="c-card__video-duration">{videoDuration}</div>}
      {hasVideo && <IconPlay modifiers={['media', 'play', 'medium', 'c-primary']} />}
      {hasGallery && <IconGallery modifiers={['media', 'gallery', 'medium', 'c-primary']} />}
    </div>
  );
};

const renderLabel = (section, channelLabel, cardLabelWrapper, showChannel, modifiers) => {
  if (!section && !channelLabel) {
    return null;
  }

  const DEFAULT_SECTION_MODIFIERS = ['article-heading'];
  const DEFAULT_CHANNEL_MODIFIERS = ['article-heading-light'];
  const isWrapping = Boolean(cardLabelWrapper);

  return (
    <Wrapper className="c-card__label-wrapper c-label-wrapper" isWrapping={isWrapping}>
      {section && <Label modifiers={modifiers || DEFAULT_SECTION_MODIFIERS}>{getLabelText(section)}</Label>}
      {showChannel && channelLabel && (
        <Label modifiers={modifiers || DEFAULT_CHANNEL_MODIFIERS}>{getLabelText(channelLabel)}</Label>
      )}
    </Wrapper>
  );
};

const renderAuthor = (name, image) => {
  if (!name || !image) {
    return null;
  }
  return (
    <div class="c-card__author">
      <div class="c-card__author-img-wrapper">
        <div class="o-image-circle o-aspect-ratio o-aspect-ratio--square">
          <img crossorigin="anonymous" class="o-image-circle__img" alt={name} title={name} src={image || ''} />
        </div>
      </div>
      <div class="c-card__author-info">
        <p class="c-card__author-name-no-intro">{name}</p>
      </div>
    </div>
  );
};

const Card = ({
  modifiers = DEFAULT_CARD_MODIFIERS,
  newsData = DEFAULT_NEWS_DATA,
  intcmpCard,
  intcmpVideo,
  target = DEFAULT_TARGET,
  imageAspectRatio = ASPECT_RATIO_16_10,
  time,
  timeOnSide,
  showChannel = DEFAULT_SHOW_CHANNEL,
  truncateTitle = DEFAULT_TRUNCATE_TITLE,
  rel,
  isParsely,
  labelModifiers,
  onClick,
}) => {
  const { cardLabelWrapper, channelLabel, section, url, urlImage, duration, abstract, disclaimer, blogAuthor } =
    newsData;
  const title = truncateTitle ? truncateText(newsData.title) : newsData.title;
  const hasVideo = Boolean(duration) || newsData.articleVideoTemplate;
  const hasGallery = newsData.galleryTemplate;
  const classNames = getClassNames('c-card', modifiers);

  const href = intcmpVideo ? getUrlWithParam('intcmp', intcmpVideo, url) : url;

  const trackings = {};

  if (intcmpCard) {
    trackings['data-intcmp'] = intcmpCard;
  }

  return (
    <a className={classNames} href={href} target={target} rel={rel} onClick={onClick} {...trackings}>
      <article className="c-card__wrapper">
        {time && timeOnSide && (
          <time className="c-card__date" dateTime={time}>
            {time}
          </time>
        )}
        <div className="c-card__content">
          <h2 className="c-card__title">{title}</h2>
          {blogAuthor && renderAuthor(blogAuthor.name, blogAuthor.image)}
          {!blogAuthor && renderLabel(section, channelLabel, cardLabelWrapper, showChannel, labelModifiers)}
          {!blogAuthor && abstract && <p className="c-card__abstract">{abstract}</p>}
          {!blogAuthor && disclaimer && (
            <div className="c-card__disclaimer-container">
              <div className="c-card__disclaimer">{disclaimer}</div>
            </div>
          )}
          {!blogAuthor && time && !timeOnSide && (
            <time className="c-card__date" dateTime={time}>
              {time}
            </time>
          )}
        </div>
        {renderImage({ urlImage, hasVideo, hasGallery, imageAspectRatio, videoDuration: duration, isParsely })}
      </article>
    </a>
  );
};

Card.propTypes = {
  modifiers: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
  newsData: PropTypes.shape({
    section: PropTypes.string,
    title: PropTypes.string.isRequired,
    abstract: PropTypes.string,
    blogAuthor: PropTypes.shape({
      name: PropTypes.string,
      image: PropTypes.string,
    }),
    disclaimer: PropTypes.string,
    time: PropTypes.string,
    url: PropTypes.string,
    urlImage: PropTypes.string,
    cardLabelWrapper: PropTypes.string,
    channelLabel: PropTypes.string,
    duration: PropTypes.string,
    articleVideoTemplate: PropTypes.bool,
    articleTemplate: PropTypes.bool,
    galleryTemplate: PropTypes.bool,
  }),
  intcmpCard: PropTypes.string,
  intcmpVideo: PropTypes.string,
  target: PropTypes.string,
  videoDuration: PropTypes.string,
  imageAspectRatio: PropTypes.oneOf([ASPECT_RATIO_16_9, ASPECT_RATIO_16_10]),
  time: PropTypes.string,
  timeOnSide: PropTypes.bool,
  showChannel: PropTypes.bool,
  truncateTitle: PropTypes.bool,
  rel: PropTypes.string,
  isParsely: PropTypes.bool,
  labelModifiers: PropTypes.array,
  onClick: PropTypes.func,
};

export default Card;
